// src/components/admin-sidebar.jsx
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/logo.jpeg";
import { FiUsers, FiActivity, FiLogOut, FiMenu } from "react-icons/fi";
import { fetchCurrentUser } from "../api/user-api";

export default function Sidebar({ visible = false, onClose = () => {}, onCollapsedChange = () => {} }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      const userInfo = await fetchCurrentUser(token);
      console.log("🧠 Sidebar user info:", userInfo);
      
      if (userInfo) {
        setUser({
          name: userInfo.name  || `${userInfo.firstName} ${userInfo.lastName}`,
          email: userInfo.email,
          profilePic: userInfo.profilePic,
        });
      }
    };

    fetchUser();
  }, []);

  const navItems = [
    { label: "Overview", icon: <FiActivity />, to: "/admin-dashboard" },
    { label: "Activity Logs", icon: <FiUsers />, to: "/activity-logs" },
    { label: "Users", icon: <FiUsers />, to: "/user-list" },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  const toggleSidebar = () => {
    const newCollapsedState = !collapsed;
    setCollapsed(newCollapsedState);
    onCollapsedChange(newCollapsedState);
  };

  return (
    <div
      className={`fixed top-0 left-0 z-50 transform transition-all duration-300 ease-in-out
        ${visible ? "translate-x-0" : "-translate-x-full"} 
        md:translate-x-0 md:relative md:translate-x-0
        ${collapsed ? "w-20" : "w-64"} h-screen bg-white shadow-md flex flex-col`}
    >
      {/* Top Section (Logo + Toggle) */}
      <div className="flex-shrink-0">
        <div className="flex items-center justify-between px-4 py-6">
          {!collapsed && <img src={logo} alt="Logo" className="w-28" />}
          <button
            onClick={toggleSidebar}
            className="text-gray-600 hover:text-orange-500"
            title={collapsed ? "Expand" : "Collapse"}
          >
            <FiMenu />
          </button>
        </div>
      </div>

      {/* Middle Section (Scrollable Menu Items, scrollbar hidden inline) */}
      <div
        className="flex-1 overflow-y-auto px-2"
        style={{
          msOverflowStyle: "none", // IE and Edge
          scrollbarWidth: "none", // Firefox
        }}
      >
        <style>
          {`
            /* Hide scrollbar for Chrome, Safari and Opera */
            div::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>
        <ul className="space-y-1">
          {navItems.map(({ label, icon, to }) => {
            const active = isActive(to);
            return (
              <Link
                key={label}
                to={to}
                onClick={onClose}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 
                  ${
                    active
                      ? "bg-orange-600 text-white font-semibold"
                      : "text-gray-700 hover:bg-orange-50 hover:text-orange-600"
                  }`}
              >
                <span className="text-lg">{icon}</span>
                {!collapsed && <span>{label}</span>}
              </Link>
            );
          })}
        </ul>
      </div>

      {/* Bottom Section (User info + Logout) */}
      <div className="flex-shrink-0 px-4 py-4 border-t">
        {user ? (
          <div className="flex items-center gap-3 mb-4">
            {!collapsed && (
              <div>
                <p className="text-sm font-semibold">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-xs text-gray-500">{user.email}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="animate-pulse flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
            {!collapsed && (
              <div>
                <div className="w-24 h-4 bg-gray-200 rounded mb-1"></div>
                <div className="w-32 h-4 bg-gray-200 rounded"></div>
              </div>
            )}
          </div>
        )}
        <button
          onClick={handleLogout}
          className="flex items-center w-full gap-3 px-3 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-all duration-200"
        >
          <FiLogOut />
          {!collapsed && "Logout"}
        </button>
      </div>
    </div>
  );
}